"use client"

import { useId, useState } from "react"
import { TriangleAlert } from "lucide-react"

import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"

import { MediaDropzone } from "./media-dropzone"
import {
  ALL_SAFE_ZONE_OPTIONS,
  SAFE_ZONES_VERIFIED_ON,
  safeAreaShare,
  type FracRect,
  type SafeZoneSpec,
} from "./safe-zone-specs"
import { useLocalMedia } from "./use-local-media"

/**
 * /tools/ad-safe-zone-checker widget: drop a creative, pick a placement, see
 * the platform's published safe area over it. Everything runs on a blob: URL —
 * no upload. Geometry and sources live in safe-zone-specs.ts.
 */

const NINE_SIXTEEN = 9 / 16
/** Meta's ads guide lists a 1% aspect-ratio tolerance for 9:16 placements. */
const RATIO_TOLERANCE = 0.01

type Fit = "fill" | "fit"

export function SafeZoneOverlay({
  safe,
  opacity,
  outline,
}: {
  safe: FracRect[]
  opacity: number
  outline: boolean
}) {
  const maskId = useId()
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1000 1000"
      preserveAspectRatio="none"
    >
      <defs>
        <mask id={maskId}>
          <rect x="0" y="0" width="1000" height="1000" fill="white" />
          {safe.map((r, i) => (
            <rect
              key={i}
              x={r.x * 1000}
              y={r.y * 1000}
              width={r.w * 1000}
              height={r.h * 1000}
              fill="black"
            />
          ))}
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="1000"
        height="1000"
        fill={`rgba(239, 68, 68, ${opacity})`}
        mask={`url(#${maskId})`}
      />
      {outline ? (
        <path
          d={unionOutline(safe)}
          fill="none"
          stroke="rgb(74, 222, 128)"
          strokeWidth={2}
          strokeDasharray="6 4"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  )
}

/**
 * SVG path for the outer boundary of a union of axis-aligned rectangles, in the
 * overlay's 0–1000 viewBox. TikTok's safe area is two adjacent rectangles (a
 * notch for the right-hand rail); stroking each one separately would draw their
 * shared edge as a boundary inside the safe region.
 */
function unionOutline(safe: FracRect[]) {
  // Round before de-duplicating: x + w can differ from a neighbour's x by
  // float error, which would add a zero-width column and a stray segment.
  const snap = (v: number) => Math.round(v * 1e6) / 1e6
  const xs = [...new Set(safe.flatMap(r => [snap(r.x), snap(r.x + r.w)]))].sort(
    (a, b) => a - b,
  )
  const ys = [...new Set(safe.flatMap(r => [snap(r.y), snap(r.y + r.h)]))].sort(
    (a, b) => a - b,
  )
  const EPS = 1e-9
  const inside = (i: number, j: number) => {
    if (i < 0 || j < 0 || i >= xs.length - 1 || j >= ys.length - 1) return false
    const cx = (xs[i] + xs[i + 1]) / 2
    const cy = (ys[j] + ys[j + 1]) / 2
    return safe.some(
      r => cx > r.x - EPS && cx < r.x + r.w + EPS && cy > r.y - EPS && cy < r.y + r.h + EPS,
    )
  }
  const s = (v: number) => (v * 1000).toFixed(2)
  const segs: string[] = []
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = 0; j < ys.length - 1; j++) {
      if (!inside(i, j)) continue
      const [x1, x2, y1, y2] = [xs[i], xs[i + 1], ys[j], ys[j + 1]]
      if (!inside(i, j - 1)) segs.push(`M${s(x1)} ${s(y1)}H${s(x2)}`)
      if (!inside(i, j + 1)) segs.push(`M${s(x1)} ${s(y2)}H${s(x2)}`)
      if (!inside(i - 1, j)) segs.push(`M${s(x1)} ${s(y1)}V${s(y2)}`)
      if (!inside(i + 1, j)) segs.push(`M${s(x2)} ${s(y1)}V${s(y2)}`)
    }
  }
  return segs.join("")
}

function bounds(safe: FracRect[]) {
  const x1 = Math.min(...safe.map(r => r.x))
  const y1 = Math.min(...safe.map(r => r.y))
  const x2 = Math.max(...safe.map(r => r.x + r.w))
  const y2 = Math.max(...safe.map(r => r.y + r.h))
  return { x1, y1, x2, y2 }
}

function SafeAreaReadout({
  spec,
  width,
  height,
}: {
  spec: SafeZoneSpec
  width: number | null
  height: number | null
}) {
  const share = safeAreaShare(spec)
  const isVertical =
    width && height
      ? Math.abs(width / height - NINE_SIXTEEN) / NINE_SIXTEEN <= RATIO_TOLERANCE
      : false
  const b = bounds(spec.safe)
  const px = (frac: number, total: number) => Math.round(frac * total)

  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <dt className="text-xs text-white/45">Safe share of the frame</dt>
        <dd className="mt-1 font-mono text-lg text-white">
          {(share * 100).toFixed(1)}%
        </dd>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <dt className="text-xs text-white/45">Your file</dt>
        <dd className="mt-1 font-mono text-lg text-white">
          {width && height ? `${width} × ${height}` : "—"}
        </dd>
      </div>
      {width && height && isVertical ? (
        <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <dt className="text-xs text-white/45">
            Safe area on your file (outer bounds, in pixels)
          </dt>
          <dd className="mt-1 font-mono text-sm text-white">
            x {px(b.x1, width)}–{px(b.x2, width)} · y {px(b.y1, height)}–
            {px(b.y2, height)}
            {spec.safe.length > 1 ? " (notched — see the outline)" : ""}
          </dd>
        </div>
      ) : null}
    </dl>
  )
}

export function SafeZoneChecker() {
  const { media, error, pick, setDimensions, clear, fail } = useLocalMedia()
  const [specId, setSpecId] = useState(ALL_SAFE_ZONE_OPTIONS[0].id)
  const [fit, setFit] = useState<Fit>("fill")
  const [opacity, setOpacity] = useState(0.45)
  const [outline, setOutline] = useState(true)

  const spec =
    ALL_SAFE_ZONE_OPTIONS.find(s => s.id === specId) ?? ALL_SAFE_ZONE_OPTIONS[0]

  const ratio =
    media?.width && media?.height ? media.width / media.height : null
  const offRatio =
    ratio !== null &&
    Math.abs(ratio - NINE_SIXTEEN) / NINE_SIXTEEN > RATIO_TOLERANCE

  const objectFit = fit === "fill" ? "object-cover" : "object-contain"

  return (
    <div className="grid gap-8 rounded-3xl border border-white/10 bg-white/[0.03] p-5 text-left sm:p-8 lg:grid-cols-[1fr_minmax(0,360px)]">
      <div className="order-2 space-y-6 lg:order-1">
        <MediaDropzone
          onFile={pick}
          hasMedia={Boolean(media)}
          fileName={media?.name}
          onClear={clear}
          error={error}
        />

        <div>
          <Label className="text-sm text-white/80">Placement</Label>
          <ToggleGroup
            type="single"
            value={specId}
            onValueChange={v => v && setSpecId(v)}
            variant="outline"
            className="mt-3 grid w-full grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {ALL_SAFE_ZONE_OPTIONS.map(option => (
              <ToggleGroupItem
                key={option.id}
                value={option.id}
                className="h-auto justify-start rounded-lg border-white/15 px-3 py-2 text-left text-sm whitespace-normal data-[state=on]:border-orange-400 data-[state=on]:bg-orange-500/15 data-[state=on]:text-white"
              >
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label className="text-sm text-white/80">
              Non-9:16 files
            </Label>
            <ToggleGroup
              type="single"
              value={fit}
              onValueChange={v => v && setFit(v as Fit)}
              variant="outline"
              className="mt-3"
            >
              <ToggleGroupItem value="fill" className="px-3 text-sm">
                Fill (crop)
              </ToggleGroupItem>
              <ToggleGroupItem value="fit" className="px-3 text-sm">
                Fit (whole file)
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div>
            <Label className="text-sm text-white/80">Overlay strength</Label>
            <Slider
              className="mt-5"
              min={0.15}
              max={0.85}
              step={0.05}
              value={[opacity]}
              onValueChange={v => setOpacity(v[0] ?? 0.45)}
              aria-label="Overlay strength"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="safe-zone-outline"
            checked={outline}
            onCheckedChange={setOutline}
          />
          <Label htmlFor="safe-zone-outline" className="text-sm text-white/70">
            Outline the safe area
          </Label>
        </div>

        <SafeAreaReadout
          spec={spec}
          width={media?.width ?? null}
          height={media?.height ?? null}
        />

        {offRatio ? (
          <p className="flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-100">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            This file is {media?.width} × {media?.height}, not 9:16. The overlay
            is drawn on a 9:16 frame; switch between Fill and Fit to see what a
            crop would cut and what letterboxing leaves empty.
          </p>
        ) : null}

        <div className="rounded-xl border border-white/10 p-4 text-sm leading-6 text-white/60">
          <p className="font-medium text-white/85">{spec.label}</p>
          <p className="mt-1">
            Top {spec.margins.top} · Bottom {spec.margins.bottom} · Left{" "}
            {spec.margins.left} · Right {spec.margins.right}
          </p>
          <p className="mt-2">{spec.note}</p>
          {spec.sources.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {spec.sources.map(source => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-400 underline-offset-4 hover:underline"
                  >
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-2 text-xs text-white/40">
            Checked against the platform source on {SAFE_ZONES_VERIFIED_ON}.
          </p>
        </div>
      </div>

      <div className="order-1 lg:order-2">
        <div className="relative mx-auto aspect-[9/16] w-full max-w-[360px] overflow-hidden rounded-2xl border border-white/15 bg-black">
          {media?.kind === "image" ? (
            <img
              src={media.url}
              alt="Your creative under the safe-zone overlay"
              className={cn("absolute inset-0 h-full w-full", objectFit)}
              onLoad={e =>
                setDimensions(
                  e.currentTarget.naturalWidth,
                  e.currentTarget.naturalHeight,
                )
              }
              onError={fail}
            />
          ) : media?.kind === "video" ? (
            <video
              src={media.url}
              className={cn("absolute inset-0 h-full w-full", objectFit)}
              controls
              muted
              playsInline
              onLoadedMetadata={e =>
                setDimensions(
                  e.currentTarget.videoWidth,
                  e.currentTarget.videoHeight,
                )
              }
              onError={fail}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-[linear-gradient(160deg,#2a211a_0%,#121110_60%,#1b1712_100%)] p-8 text-center text-sm leading-6 text-white/45">
              Drop a 9:16 image or video frame to see what the interface would
              cover.
            </div>
          )}
          <SafeZoneOverlay
            safe={spec.safe}
            opacity={opacity}
            outline={outline}
          />
        </div>
        <p className="mx-auto mt-3 max-w-[360px] text-center text-xs leading-5 text-white/45">
          Red = keep text, logos and faces out. The overlay marks the margins
          each platform publishes; it does not draw the platform&rsquo;s
          interface.
        </p>
      </div>
    </div>
  )
}
